import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { 
  StarIcon as StarSolidIcon, 
  AcademicCapIcon, 
  ClockIcon, 
  BookOpenIcon, 
  UserGroupIcon, 
  CalendarIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  TrophyIcon,
  DocumentTextIcon,
  CheckCircleIcon
} from '@heroicons/react/24/solid'
import { StarIcon, ArrowLeftIcon } from '@heroicons/react/24/outline'
import UserAvatar from '../../components/UserAvatar'

interface TeacherProfile {
  id: string
  title: string
  introduction: string
  specialties: string[]
  rating: number
  status: string
  bio: string
  experience: string
  education: string
  phoneNumber?: string
  highestDegree?: string
  graduatedUniversity?: string
  graduationYear?: number
  major?: string
  teachingExperience?: string
  user: {
    firstName: string
    lastName: string
    avatar?: string
    email: string
    phone?: string
  }
  courses: Course[]
  statistics: {
    priceRange: {
      min: number
      max: number
      average: number
    }
    yearlyStats: {
      year: number
      totalCourses: number
      totalStudents: number
      totalHours: number
      completedBookings: number
    }
    ratingStats?: {
      average: number
      count: number
      distribution: {
        5: number
        4: number
        3: number
        2: number
        1: number
      }
    }
    subjectStats: Record<string, {
      courseCount: number
      studentCount: number
      totalHours: number
    }>
    totalStats: {
      allTimeCourses: number
      allTimeStudents: number
      allTimeHours: number
      totalCompletedBookings: number
    }
  }
}

interface Course {
  id: string
  title: string
  description: string
  subject: string
  finalPricePerHour?: number
  price?: number
  duration: number
  status: string
  _count: {
    bookings: number
    completedBookings: number
  }
}

interface Achievement {
  id: string
  student: {
    firstName: string
    lastName: string
    avatar?: string
  }
  course: {
    title: string
    subject: string
  }
  achievementType: string
  title: string
  description?: string
  score?: number
  maxScore?: number
  grade?: string
  rank?: number
  totalParticipants?: number
  achievementDate: string
  academicYear: number
  semester: string
}

const TeacherProfileDetail = () => {
  const { id } = useParams<{ id: string }>()
  const [teacher, setTeacher] = useState<TeacherProfile | null>(null)
  const [achievements, setAchievements] = useState<Achievement[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())

  useEffect(() => {
    if (id) {
      fetchTeacherProfile()
      fetchAchievements()
    }
  }, [id, selectedYear])

  const fetchTeacherProfile = async () => {
    try {
      const response = await fetch(`/api/teachers/${id}/profile`)
      if (response.ok) {
        const data = await response.json()
        setTeacher(data)
      }
    } catch (error) {
      console.error('获取教师资料失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchAchievements = async () => {
    try {
      const response = await fetch(`/api/achievements/teacher/${id}?year=${selectedYear}`)
      if (response.ok) {
        const data = await response.json()
        setAchievements(data)
      }
    } catch (error) {
      console.error('获取学生成绩失败:', error)
    }
  }

  const getAchievementTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      exam: '考试',
      competition: '竞赛',
      certification: '认证',
      grade_improvement: '成绩提升',
      other: '其他'
    }
    return labels[type] || type
  }

  const getSubjectColor = (subject: string) => {
    const colors: Record<string, string> = {
      '数学': 'bg-blue-100 text-blue-800',
      '英语': 'bg-green-100 text-green-800',
      '物理': 'bg-purple-100 text-purple-800',
      '化学': 'bg-yellow-100 text-yellow-800',
      '生物': 'bg-pink-100 text-pink-800',
      '历史': 'bg-indigo-100 text-indigo-800',
      '地理': 'bg-teal-100 text-teal-800',
      '政治': 'bg-red-100 text-red-800'
    }
    return colors[subject] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!teacher) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">教师资料不存在</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 返回按钮 */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            to="/teachers"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            返回教师列表
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 教师基本信息 */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-8">
            {/* 头像 */}
            <div className="flex-shrink-0">
              <UserAvatar
                user={{
                  firstName: teacher.user.firstName,
                  lastName: teacher.user.lastName,
                  avatar: teacher.user.avatar
                }}
                size="xl"
              />
            </div>

            {/* 基本信息 */}
            <div className="flex-1 text-center lg:text-left">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {teacher.user.firstName} {teacher.user.lastName}
              </h1>
              <p className="text-xl text-gray-600 mb-4">{teacher.title}</p>

              {/* 评分 */}
              {teacher.statistics.ratingStats && (
                <div className="flex items-center justify-center lg:justify-start mb-4">
                  <div className="flex items-center">
                    {[0, 1, 2, 3, 4].map((rating) => (
                      <StarSolidIcon
                        key={rating}
                        className={`${
                          teacher.statistics.ratingStats!.average > rating ? 'text-yellow-400' : 'text-gray-300'
                        } h-5 w-5 flex-shrink-0`}
                      />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-gray-600">
                    {(Number(teacher.statistics.ratingStats.average) || 0).toFixed(1)} ({teacher.statistics.ratingStats.count} 评价)
                  </span>
                </div>
              )}

              {/* 价格区间 */}
              <div className="mb-4">
                <div className="inline-flex items-center px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
                  <CurrencyDollarIcon className="h-5 w-5 text-green-600 mr-2" />
                  <span className="text-green-800 font-medium">
                    价格区间: ¥{teacher.statistics.priceRange.min}/小时 - ¥{teacher.statistics.priceRange.max}/小时
                  </span>
                </div>
              </div>

              {/* 简介 */}
              <p className="text-gray-600 mb-6">{teacher.introduction}</p>

              {/* 专长标签 */}
              <div className="flex flex-wrap gap-2 justify-center lg:justify-start mb-6">
                {teacher.specialties.map((specialty, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                  >
                    {specialty}
                  </span>
                ))}
              </div>

              {/* 年度统计卡片 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{teacher.statistics.yearlyStats.totalCourses}</div>
                  <div className="text-sm text-blue-600">开设课程</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{teacher.statistics.yearlyStats.totalStudents}</div>
                  <div className="text-sm text-green-600">年度学生</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">{teacher.statistics.yearlyStats.totalHours}</div>
                  <div className="text-sm text-purple-600">教学时长</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">{achievements.length}</div>
                  <div className="text-sm text-yellow-600">学生成绩</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 标签导航 */}
        <div className="bg-white rounded-lg shadow-sm mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { key: 'overview', label: '总览', icon: ChartBarIcon },
                { key: 'courses', label: '课程详情', icon: BookOpenIcon },
                { key: 'achievements', label: '学生成绩', icon: TrophyIcon },
                { key: 'reviews', label: '评价统计', icon: StarIcon }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`${
                    activeTab === tab.key
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <tab.icon className="h-5 w-5 mr-2" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* 标签内容 */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* 教育背景 */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <AcademicCapIcon className="h-5 w-5 mr-2 text-gray-400" />
                  教育背景
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm font-medium text-gray-500">最高学历:</span>
                      <p className="text-gray-900">{teacher.highestDegree || '未提供'}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">毕业院校:</span>
                      <p className="text-gray-900">{teacher.graduatedUniversity || '未提供'}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">毕业年份:</span>
                      <p className="text-gray-900">{teacher.graduationYear || '未提供'}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">专业:</span>
                      <p className="text-gray-900">{teacher.major || '未提供'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 科目统计 */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">科目教学统计</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(teacher.statistics.subjectStats).map(([subject, stats]) => (
                    <div key={subject} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSubjectColor(subject)}`}>
                          {subject}
                        </span>
                      </div>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">课程数:</span>
                          <span className="font-medium">{stats.courseCount}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">学生数:</span>
                          <span className="font-medium">{stats.studentCount}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">教学时长:</span>
                          <span className="font-medium">{stats.totalHours}小时</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 个人简介 */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">个人简介</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{teacher.bio || '暂无个人简介'}</p>
                </div>
              </div>

              {/* 教学经验 */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">教学经验</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{teacher.teachingExperience || '暂无教学经验描述'}</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'courses' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-6">开设课程</h3>
              {teacher.courses.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {teacher.courses.map((course) => (
                    <div key={course.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSubjectColor(course.subject)}`}>
                          {course.subject}
                        </span>
                        <div className="text-right">
                          <div className="text-lg font-bold text-gray-900">
                            ¥{course.finalPricePerHour || course.price}/小时
                          </div>
                        </div>
                      </div>
                      <h4 className="text-lg font-medium text-gray-900 mb-2">{course.title}</h4>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3">{course.description}</p>
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {course.duration}小时
                        </div>
                        <div className="flex items-center">
                          <UserGroupIcon className="h-4 w-4 mr-1" />
                          {course._count.completedBookings}人完成
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <BookOpenIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">暂无课程</h3>
                  <p className="mt-1 text-sm text-gray-500">该教师还没有开设任何课程</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'achievements' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">学生成绩</h3>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                    <option key={year} value={year}>{year}年</option>
                  ))}
                </select>
              </div>

              {achievements.length > 0 ? (
                <div className="space-y-4">
                  {achievements.map((achievement) => (
                    <div key={achievement.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3">
                          <UserAvatar
                            user={achievement.student}
                            size="sm"
                          />
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h4 className="font-medium text-gray-900">
                                {achievement.student.firstName} {achievement.student.lastName}
                              </h4>
                              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                {getAchievementTypeLabel(achievement.achievementType)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-1">
                              课程: {achievement.course.title} ({achievement.course.subject})
                            </p>
                            <p className="text-sm font-medium text-gray-900 mb-2">{achievement.title}</p>
                            {achievement.description && (
                              <p className="text-sm text-gray-600 mb-2">{achievement.description}</p>
                            )}
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              <span>{new Date(achievement.achievementDate).toLocaleDateString('zh-CN')}</span>
                              <span>{achievement.academicYear}学年</span>
                              <span>{achievement.semester === 'spring' ? '春季' : 
                                     achievement.semester === 'fall' ? '秋季' : 
                                     achievement.semester === 'summer' ? '夏季' : 
                                     achievement.semester === 'winter' ? '冬季' : '全年'}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end space-y-1">
                          {achievement.score && (
                            <div className="text-lg font-bold text-green-600">
                              {achievement.score}{achievement.maxScore && `/${achievement.maxScore}`}
                            </div>
                          )}
                          {achievement.grade && (
                            <div className="inline-flex items-center px-2 py-1 rounded text-sm font-medium bg-green-100 text-green-800">
                              {achievement.grade}
                            </div>
                          )}
                          {achievement.rank && (
                            <div className="text-sm text-gray-600">
                              第{achievement.rank}名{achievement.totalParticipants && `/${achievement.totalParticipants}人`}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <TrophyIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">暂无学生成绩</h3>
                  <p className="mt-1 text-sm text-gray-500">{selectedYear}年还没有学生成绩记录</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'reviews' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-6">评价统计</h3>
              {teacher.statistics.ratingStats ? (
                <div className="space-y-6">
                  {/* 评分概览 */}
                  <div className="flex items-center space-x-8">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-gray-900">{(Number(teacher.statistics.ratingStats.average) || 0).toFixed(1)}</div>
                      <div className="flex items-center justify-center mt-1">
                        {[0, 1, 2, 3, 4].map((rating) => (
                          <StarSolidIcon
                            key={rating}
                            className={`${
                              (Number(teacher.statistics.ratingStats!.average) || 0) > rating ? 'text-yellow-400' : 'text-gray-300'
                            } h-5 w-5`}
                          />
                        ))}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">{teacher.statistics.ratingStats.count} 评价</div>
                    </div>
                  </div>

                  {/* 评分分布 */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-4">评分分布</h4>
                    <div className="space-y-3">
                      {[5, 4, 3, 2, 1].map((stars) => {
                        const count = teacher.statistics.ratingStats!.distribution[stars as keyof typeof teacher.statistics.ratingStats.distribution];
                        const percentage = teacher.statistics.ratingStats!.count > 0 ? (count / teacher.statistics.ratingStats!.count) * 100 : 0;
                        return (
                          <div key={stars} className="flex items-center space-x-4">
                            <div className="flex items-center space-x-1 w-16">
                              <span className="text-sm text-gray-600">{stars}</span>
                              <StarSolidIcon className="h-4 w-4 text-yellow-400" />
                            </div>
                            <div className="flex-1 bg-gray-200 rounded-full h-3">
                              <div
                                className="bg-yellow-400 h-3 rounded-full transition-all duration-300"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                            <div className="text-sm text-gray-600 w-12 text-right">{count}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <StarIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">暂无评价</h3>
                  <p className="mt-1 text-sm text-gray-500">该教师还没有收到任何评价</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TeacherProfileDetail 